import axios from 'axios';
import type { TFunction } from 'i18next';

export interface ApiError {
  status: number;
  message: string;
  code?: string;
  details?: unknown;
}

const statusMessageKeys: Record<number, string> = {
  400: 'error.apiError400',
  401: 'error.apiError401',
  403: 'error.apiError403',
  404: 'error.apiError404',
  409: 'error.apiError409',
  500: 'error.apiError500',
};

let errorTranslator: TFunction | null = null;

export function setErrorTranslator(t: TFunction): void {
  errorTranslator = t;
}

function translateError(key: string): string {
  return errorTranslator ? errorTranslator(key) : key;
}

function getMessageFromData(data: unknown): string | undefined {
  if (!data) {
    return undefined;
  }

  if (typeof data === 'string') {
    return data;
  }

  if (typeof data === 'object') {
    const responseData = data as {
      message?: unknown;
      error?: unknown;
      title?: unknown;
    };

    if (typeof responseData.message === 'string') {
      return responseData.message;
    }

    if (typeof responseData.error === 'string') {
      return responseData.error;
    }

    if (typeof responseData.title === 'string') {
      return responseData.title;
    }
  }

  return undefined;
}

function getCodeFromData(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') {
    return undefined;
  }

  const responseData = data as {
    code?: unknown;
  };

  if (typeof responseData.code === 'string') {
    return responseData.code;
  }

  return undefined;
}

export function toApiError(error: unknown, t?: TFunction): ApiError {
  const translate = t ? (key: string) => t(key) : translateError;

  if (axios.isAxiosError(error)) {
    const status = error.response?.status ?? 500;
    const data = error.response?.data;

    return {
      status,
      code: getCodeFromData(data),
      details: data,
      message:
        getMessageFromData(data) ??
        (statusMessageKeys[status]
          ? translate(statusMessageKeys[status])
          : translate('apiErrorGeneric')),
    };
  }

  if (error instanceof Error) {
    return {
      status: 500,
      message: error.message,
    };
  }

  return {
    status: 500,
    message: translate('error.apiErrorUnknown'),
  };
}
